import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useUser } from "../../Context/UserContext";
import { DataGrid, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Link } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Utility function to calculate required width
const calculateColumnWidth = (headerName, minWidth = 120) => {
  const calculatedWidth = Math.ceil(headerName.length * 8.5) + 50;
  return Math.max(minWidth, calculatedWidth);
};

// Color definitions matching your existing theme
const COLORS = {
  summaryGroup: "FFD9EAD3", // Green background
  saplingsGroup: "FFF1C27D", // Orange background
  headerBackground: "FFE8EAF6", // Light purple for column headers
  headerFont: "00000000", // Black font
  border: "FFBDBDBD", // Light gray border
};

const DepartmentReport = () => {
  const [reportData, setReportData] = useState([]);
  const [allSpeciesList, setAllSpeciesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const { token } = useUser();
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get("/api/center/department-summary/", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setReportData(res.data.departments || []);
        setAllSpeciesList(res.data.allSpecies || []);
      })
      .catch((err) => console.error("Error:", err))
      .finally(() => setLoading(false));
  }, [token]);

  const { columns, columnGroupingModel } = useMemo(() => {
    const baseColumns = [
      {
        field: "department_name",
        headerName: "Department Name",
        width: 200,
        pinning: "left",
        renderCell: (params) => (
          <span style={{ fontWeight: 600, color: "#333" }}>{params.value}</span>
        ),
      },
      {
        field: "district_count",
        headerName: "Districts",
        width: 120,
        pinning: "left",
        renderCell: (params) => (
          <Link
            to={`/department/${params.row.id}/DistrictReport`}
            style={{
              textDecoration: "none",
              color: "#1976d2",
              fontWeight: "500",
            }}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: "production_center_count",
        headerName: "Prod. Centers",
        width: 150,
        pinning: "left",
      },
    ];

    const summaryColumns = [
      {
        field: "species_count",
        headerName: "Species Count",
        width: 150,
        pinning: "right",
      },
      {
        field: "total_target",
        headerName: "Target",
        width: 120,
        pinning: "right",
      },
      {
        field: "total_stock_saplings",
        headerName: "Stock",
        width: 120,
        pinning: "right",
      },
      {
        field: "total_selled",
        headerName: "Total Sold",
        width: 140,
        pinning: "right",
      },
      {
        field: "total_selled_price",
        headerName: "Total Sold Price",
        width: 170,
        pinning: "right",
      },
    ];

    const speciesColumns = allSpeciesList.map((s) => {
      const name = s.name || s.species_name || "";
      return {
        field: `species_${s.id}`,
        headerName: name,
        width: calculateColumnWidth(name, 120),
        type: "number",
        valueGetter: (value, row) =>
          row?.saplingLookup?.[String(s.id)]?.qty || 0,
      };
    });

    const groups = [
      {
        groupId: "Summary",
        children: summaryColumns.map((c) => ({ field: c.field })),
        headerClassName: "summary-group-header",
      },
      {
        groupId: "Saplings",
        children: speciesColumns.map((s) => ({ field: s.field })),
        headerClassName: "saplings-group-header",
      },
    ];

    return {
      columns: [...baseColumns, ...summaryColumns, ...speciesColumns],
      columnGroupingModel: groups,
    };
  }, [allSpeciesList]);

  // ✅ Excel Export Function
  const exportToExcel = useCallback(async () => {
    setExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Department Report", {
        properties: { tabColor: { argb: "FF1976D2" } },
      });

      const baseColumns = [
        { header: "Department Name", field: "department_name" },
        { header: "Districts", field: "district_count" },
        { header: "Prod. Centers", field: "production_center_count" },
      ];

      const summaryColumns = [
        { header: "Species Count", field: "species_count" },
        { header: "Target", field: "total_target" },
        { header: "Stock", field: "total_stock_saplings" },
        { header: "Total Sold", field: "total_selled" },
        { header: "Total Sold Price", field: "total_selled_price" },
      ];

      const speciesColumns = allSpeciesList.map((s) => ({
        header: s.name || s.species_name || "",
        field: `species_${s.id}`,
        getValue: (row) => row?.saplingLookup?.[String(s.id)]?.qty || 0,
      }));

      const allColumns = [...baseColumns, ...summaryColumns, ...speciesColumns];
      const baseColCount = baseColumns.length;
      const summaryColCount = summaryColumns.length;
      const speciesColCount = speciesColumns.length;

      // ROW 1: Group Headers
      worksheet.mergeCells(1, 1, 1, baseColCount);
      const deptCell = worksheet.getCell(1, 1);
      deptCell.value = "Department Info";
      deptCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9EAD3" },
      };
      deptCell.font = {
        bold: true,
        size: 12,
        color: { argb: COLORS.headerFont },
      };
      deptCell.alignment = { horizontal: "center", vertical: "middle" };
      deptCell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };

      const summaryStart = baseColCount + 1;
      const summaryEnd = baseColCount + summaryColCount;
      worksheet.mergeCells(1, summaryStart, 1, summaryEnd);
      const sumCell = worksheet.getCell(1, summaryStart);
      sumCell.value = "Summary";
      sumCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.summaryGroup },
      };
      sumCell.font = {
        bold: true,
        size: 12,
        color: { argb: COLORS.headerFont },
      };
      sumCell.alignment = { horizontal: "center", vertical: "middle" };
      sumCell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };

      if (speciesColCount > 0) {
        const sapStart = summaryEnd + 1;
        const sapEnd = summaryEnd + speciesColCount;
        worksheet.mergeCells(1, sapStart, 1, sapEnd);
        const sapCell = worksheet.getCell(1, sapStart);
        sapCell.value = "Saplings";
        sapCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: COLORS.saplingsGroup },
        };
        sapCell.font = {
          bold: true,
          size: 12,
          color: { argb: COLORS.headerFont },
        };
        sapCell.alignment = { horizontal: "center", vertical: "middle" };
        sapCell.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
      }

      // ROW 2: Column Headers
      const headerRow = worksheet.addRow(allColumns.map((c) => c.header));
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: COLORS.headerBackground },
        };
        cell.font = {
          bold: true,
          size: 10,
          color: { argb: COLORS.headerFont },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "medium" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // DATA ROWS
      reportData.forEach((row, rowIndex) => {
        const rowData = allColumns.map((col) =>
          col.getValue ? col.getValue(row) : row[col.field],
        );
        const dataRow = worksheet.addRow(rowData);
        const isEven = rowIndex % 2 === 0;

        dataRow.eachCell((cell, colNumber) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: isEven ? "FFFFFFFF" : "FFF5F5F5" },
          };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };

          if (typeof cell.value === "number") {
            cell.alignment = { horizontal: "right", vertical: "middle" };
            cell.numFmt = colNumber === summaryEnd ? "#,##0.00" : "#,##0";
          } else {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          }
        });
      });

      // COLUMN WIDTHS
      worksheet.columns.forEach((column, index) => {
        let maxWidth = allColumns[index].header.length + 4;
        reportData.slice(0, 10).forEach((row) => {
          const col = allColumns[index];
          const value = col.getValue ? col.getValue(row) : row[col.field];
          if (value !== null && value !== undefined)
            maxWidth = Math.max(maxWidth, String(value).length + 2);
        });
        column.width = Math.min(maxWidth * 1.2, 30);
      });

      worksheet.views = [
        { state: "frozen", xSplit: 3, ySplit: 2, topLeftCell: "D3" },
      ];
      worksheet.autoFilter = {
        from: { row: 2, column: 1 },
        to: { row: 2, column: allColumns.length },
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(
        blob,
        `Department_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (error) {
      console.error("Excel export error:", error);
      alert("Failed to export Excel file.");
    } finally {
      setExporting(false);
    }
  }, [reportData, allSpeciesList]);

  return (
    <Box
      sx={{
        height: "calc(100vh - 60px)",
        width: "97.5%",
        display: "flex",
        flexDirection: "column",
        p: 2,
        backgroundColor: "#f4f6f8",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          alignItems: "center",
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by Department..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: "300px", backgroundColor: "white", borderRadius: "4px" }}
        />
        <Button
          variant="contained"
          color="success"
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}
          disabled={exporting || loading}
          sx={{ minWidth: 180, gap: 1 }}
        >
          {exporting ? "Exporting..." : "Download Excel"}
        </Button>
      </Box>

      <Box
        sx={{ flexGrow: 1, bgcolor: "white", borderRadius: 1, boxShadow: 3 }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={reportData}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          loading={loading}
          density="compact"
          rowHeight={38}
          disableRowSelectionOnClick
          filterModel={{
            items: [
              {
                field: "department_name",
                operator: "contains",
                value: searchText,
              },
            ],
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              whiteSpace: "nowrap",
            },
            "& .saplings-group-header .MuiDataGrid-columnHeaderTitleContainer":
              {
                backgroundColor: "#f1c27d",
                justifyContent: "center",
              },
            "& .summary-group-header .MuiDataGrid-columnHeaderTitleContainer": {
              backgroundColor: "#d9ead3",
              justifyContent: "center",
            },
          }}
          slots={{ toolbar: GridToolbar }}
          pinnedColumns={{
            left: [
              "department_name",
              "district_count",
              "production_center_count",
            ],
            right: [
              "species_count",
              "total_target",
              "total_stock_saplings",
              "total_selled",
              "total_selled_price",
            ],
          }}
        />
      </Box>
    </Box>
  );
};

export default DepartmentReport;
